import {
	Button,
	Checkbox,
	EditIcon,
	Loader,
	MapLabelsIcon,
	MapLayerIcon,
	MapMarkersIcon,
	PopoverButton,
	RulerIcon,
	SearchIcon,
	SearchInput,
	ZoomInIcon,
	ZoomOutIcon,
} from '@kit';

import dynamic from 'next/dynamic';
import React, {useCallback, useMemo, useRef, useState} from 'react';

import {NotFound} from '../../../components/not-found';
import {cls, getApolloError} from '../../../components/utils';
import {useCreateMapLabelMutation, useGameMapDataQuery, useGameMapQuery, useUpdateMapGeometryMutation} from '../../../generated/types';
import {useUser} from '../../../components/use-user';
import {buildUrl} from '../../../lib/routes';
import {NotFoundSeo, Seo, pageJsonLd, sectionCrumbs} from '../../../lib/seo';
import {MapLabelCard} from './map-label-card';
import {MapFilter} from './map-filter';
import {MapScale, milesText} from './map-scale';
import type {MapApi, MapLayers, MapViewState} from './map-canvas';

import css from './map-view.module.css';

const MapCanvas = dynamic(() => import('./map-canvas').then((m) => m.MapCanvas), {
	ssr: false,
	loading: () => <Loader />,
});

const SEARCH_LIMIT = 8;

const normalize = (text: string) => text.toLowerCase().replace(/ё/g, 'е').replace(/[’'`]/g, '');

type LayerButton = {key: keyof MapLayers; title: string; icon: React.ReactElement};

const LAYER_BUTTONS: LayerButton[] = [
	{key: 'labels', title: 'Подписи', icon: <MapLabelsIcon />},
	{key: 'markers', title: 'Метки', icon: <MapMarkersIcon />},
];

const OVERLAY_LAYERS: Array<{key: keyof MapLayers; title: string}> = [
	{key: 'hexes', title: 'Гексы: 1 гекс = 24 мили'},
	{key: 'regions', title: 'Регионы'},
	{key: 'underdark', title: 'Подземье'},
];

type Props = {id: string};

export const MapView: React.FC<Props> = ({id}) => {
	const {data: metaData, loading: metaLoading, error: metaError} = useGameMapQuery({variables: {id}, ssr: true});
	const {data, error} = useGameMapDataQuery({variables: {id}, ssr: false, fetchPolicy: 'cache-and-network'});

	const [layers, setLayers] = useState<MapLayers>({labels: true, markers: true, roads: true, regions: false, hexes: false, underdark: false});
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [focus, setFocus] = useState<{id: number; nonce: number} | null>(null);
	const [searchOpen, setSearchOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [active, setActive] = useState(0);
	const [hidden, setHidden] = useState<Set<string>>(() => new Set());
	const toggleHidden = useCallback((key: string) => setHidden((current) => {
		const next = new Set(current);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		return next;
	}), []);
	const [measuring, setMeasuring] = useState(false);
	const [measured, setMeasured] = useState<number | null>(null);
	const [view, setView] = useState<MapViewState | null>(null);
	const apiRef = useRef<MapApi | null>(null);
	const user = useUser();
	const [editing, setEditing] = useState(false);
	const [editState, setEditState] = useState({dirty: 0, tooMany: false, selected: 0});
	const [saveGeometry, {loading: saving, error: saveError}] = useUpdateMapGeometryMutation({refetchQueries: ['GameMapData']});
	const [placing, setPlacing] = useState(false);
	const [editCardId, setEditCardId] = useState<number | null>(null);
	const [createLabel, {loading: creating, error: createError}] = useCreateMapLabelMutation({refetchQueries: ['GameMapData'], awaitRefetchQueries: true});

	const map = metaData?.gameMap;
	const labels = useMemo(() => data?.gameMap?.labels || [], [data]);
	const paths = useMemo(() => data?.gameMap?.paths || [], [data]);
	const selected = labels.find((label) => label.id === selectedId) || null;
	const hasUnderdark = useMemo(() => labels.some((label) => label.layer === 'underdark'), [labels]);
	const hasRoads = paths.length > 0;
	const hasRegions = useMemo(() => labels.some((label) => label.kind !== 'underdark-area' && !!label.polygon), [labels]);
	const hasMarkers = useMemo(() => labels.some((label) => label.layer !== 'underdark' && label.markerX != null && label.marker), [labels]);
	const roadKinds = useMemo(() => new Set(paths.map((path) => path.kind)), [paths]);

	const results = useMemo(() => {
		const q = normalize(query.trim());
		if (q.length < 2) return [];
		const seen = new Set<string>();
		return labels
			.filter((label) => label.title && (normalize(label.title).includes(q) || normalize(label.originalTitle).includes(q)))
			.sort((a, b) => Number(!normalize(a.title).startsWith(q)) - Number(!normalize(b.title).startsWith(q)) || b.size - a.size)
			.filter((label) => {
				const key = `${label.title}|${label.kind}`;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			})
			.slice(0, SEARCH_LIMIT);
	}, [labels, query]);

	const pick = useCallback(
		(labelId: number) => {
			const label = labels.find((item) => item.id === labelId);
			if (label?.layer) setLayers((current) => ({...current, underdark: label.layer === 'underdark'}));
			setSelectedId(labelId);
			setFocus({id: labelId, nonce: Date.now()});
			setQuery('');
			setActive(0);
		},
		[labels],
	);

	const placeLabel = useCallback(
		async (x: number, y: number) => {
			if (!map) return;
			setPlacing(false);
			const layer = layers.underdark ? 'underdark' : 'surface';
			const result = await createLabel({variables: {mapId: map.id, label: {title: 'Новая метка', kind: 'building', marker: 'misc', x, y, size: 12, layer}}});
			const id = result.data?.createMapLabel.id;
			if (id) {
				setSelectedId(id);
				setEditCardId(id);
			}
		},
		[map, layers.underdark, createLabel],
	);

	const handleSearchKey = (event: React.KeyboardEvent) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setActive((index) => (results.length ? (index + 1) % results.length : 0));
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActive((index) => (results.length ? (index - 1 + results.length) % results.length : 0));
		} else if (event.key === 'Enter' && results[active]) {
			event.preventDefault();
			pick(results[active].id);
		} else if (event.key === 'Escape') {
			setQuery('');
			setSearchOpen(false);
		}
	};

	const toggleLayer = (key: keyof MapLayers) => setLayers((current) => ({...current, [key]: !current[key]}));

	const handleReady = useCallback((api: MapApi) => {
		apiRef.current = api;
	}, []);

	if (!metaLoading && !map) {
		return (
			<>
				<NotFoundSeo />
				{metaError ? <div>{getApolloError(metaError).message}</div> : <NotFound />}
			</>
		);
	}
	if (!map) return <Loader />;

	const path = buildUrl('map', {id: map.id});
	const title = `Карта: ${map.title} (${map.originalTitle}) — интерактивная карта`;
	const description =
		map.description || `Интерактивная карта «${map.title}» на русском: города, дороги, реки, леса и горы с переводом названий.`;

	const regionsTitle = labels.some((label) => label.kind === 'ward' && !!label.polygon) ? 'Районы' : 'Регионы';
	const available: Record<keyof MapLayers, boolean> = {
		labels: true,
		markers: hasMarkers,
		roads: hasRoads,
		regions: hasRegions,
		hexes: !!map.milesPerPixel,
		underdark: hasUnderdark,
	};

	return (
		<div className={css.root}>
			<Seo
				title={title}
				description={description}
				canonicalPath={path}
				jsonLd={pageJsonLd({
					path,
					title,
					description,
					pageType: 'WebPage',
					dateModified: map.updatedAt,
					crumbs: sectionCrumbs('maps', {name: map.title, path}),
				})}
			/>
			<h1 className={css.hiddenTitle}>Карта: {map.title}</h1>
			{data?.gameMap && (
				<MapCanvas
					map={map}
					labels={labels}
					paths={paths}
					layers={layers}
					hidden={hidden}
					selectedId={selectedId}
					onSelect={setSelectedId}
					focus={focus}
					measuring={measuring}
					onMeasure={setMeasured}
					onReady={handleReady}
					onViewChange={setView}
					editing={editing}
					onEditState={setEditState}
					onPlace={placing ? placeLabel : null}
				/>
			)}
			{error && <div className={css.error}>{getApolloError(error).message}</div>}

			<div className={css.toolbarArea}>
				<div className={css.toolbar} role="toolbar" aria-label="Карта">
					<Button
						appearance={searchOpen ? 'primary' : 'subtle'}
						icon={<SearchIcon />}
						title="Поиск по карте"
						aria-label="Поиск по карте"
						aria-pressed={searchOpen}
						onClick={() => setSearchOpen((open) => !open)}
					/>
					<span className={css.separator} />
					{LAYER_BUTTONS.filter((button) => available[button.key]).map((button) => (
						<Button
							key={button.key}
							appearance={layers[button.key] ? 'primary' : 'subtle'}
							icon={button.icon}
							title={button.title}
							aria-label={button.title}
							aria-pressed={layers[button.key]}
							onClick={() => toggleLayer(button.key)}
						/>
					))}
					<MapFilter labels={labels} roadKinds={roadKinds} hidden={hidden} onToggle={toggleHidden} underdark={layers.underdark} />
					{OVERLAY_LAYERS.some((layer) => available[layer.key]) && (
						<PopoverButton
							appearance={OVERLAY_LAYERS.some((layer) => available[layer.key] && layers[layer.key]) ? 'primary' : 'subtle'}
							icon={<MapLayerIcon />}
							title="Слои"
							aria-label="Слои"
							popover={
								<div className={css.layersMenu}>
									{OVERLAY_LAYERS.filter((layer) => available[layer.key]).map((layer) => (
										<Checkbox
											key={layer.key}
											label={layer.key === 'regions' ? regionsTitle : layer.title}
											checked={layers[layer.key]}
											onChange={() => toggleLayer(layer.key)}
										/>
									))}
								</div>
							}
						/>
					)}
					<span className={css.separator} />
					{map.milesPerPixel && (
						<Button
							appearance={measuring ? 'primary' : 'subtle'}
							icon={<RulerIcon />}
							title="Линейка: щёлкайте по карте, чтобы проложить маршрут"
							aria-label="Линейка"
							aria-pressed={measuring}
							onClick={() => setMeasuring((on) => !on)}
						/>
					)}
					{user?.isAdmin && (
						<>
							<Button
								appearance={editing ? 'primary' : 'subtle'}
								icon={<EditIcon />}
								title="Правка границ регионов и дорог"
								aria-label="Правка"
								aria-pressed={editing}
								onClick={() => {
									if (editing) apiRef.current?.revertGeometry();
									setPlacing(false);
									setEditing((on) => !on);
								}}
							/>
						</>
					)}
					<span className={css.separator} />
					<Button appearance="subtle" icon={<ZoomOutIcon />} title="Отдалить" aria-label="Отдалить" onClick={() => apiRef.current?.zoomOut()} />
					<Button appearance="subtle" icon={<ZoomInIcon />} title="Приблизить" aria-label="Приблизить" onClick={() => apiRef.current?.zoomIn()} />
				</div>

				{searchOpen && (
					<div className={css.panel}>
						<SearchInput
							autoFocus
							value={query}
							onChange={(value) => {
								setQuery(value);
								setActive(0);
							}}
							onKeyDown={handleSearchKey}
							placeholder="Город, река, лес…"
							aria-label="Найти на карте"
							aria-controls="map-search-results"
							aria-activedescendant={results[active] ? `map-result-${results[active].id}` : undefined}
							appearance="outline"
						/>
						{results.length > 0 && (
							<ul className={css.results} id="map-search-results" role="listbox">
								{results.map((label, index) => (
									<li key={label.id} id={`map-result-${label.id}`} role="option" aria-selected={index === active}>
										<button
											type="button"
											tabIndex={-1}
											className={cls(css.result, index === active && css.resultActive)}
											onMouseEnter={() => setActive(index)}
											onClick={() => pick(label.id)}
										>
											<span>{label.title}</span>
											<span className={css.resultOriginal}>{label.originalTitle}</span>
										</button>
									</li>
								))}
							</ul>
						)}
					</div>
				)}

			</div>

			<div className={css.bottomArea}>
				{measuring && (
					<div className={css.measure}>
						{measured ? <MeasureText miles={measured} /> : 'Щёлкните по карте, чтобы поставить первую точку'}
					</div>
				)}
				{editing && (
					<div className={css.measure}>
						<div>
							{editState.tooMany
								? 'Приблизьте карту, чтобы появились точки'
								: editState.selected
									? `Выделено точек: ${editState.selected}. Тяните любую из них, Delete — удалить, Esc — снять выделение`
									: 'Тяните точки. Shift+рамка — выделить несколько (с Ctrl — добавить), Ctrl+щелчок — выделить точку, Alt+щелчок — удалить, Shift+щелчок по линии — добавить точку'}
						</div>
						{placing && <div>Щёлкните по карте там, где должна стоять новая метка</div>}
						{saveError && <div className={css.error}>{getApolloError(saveError).message}</div>}
						{createError && <div className={css.error}>{getApolloError(createError).message}</div>}
						<div className={css.cardButtons}>
							<Button
								appearance="primary"
								loading={saving}
								disabled={!editState.dirty}
								onClick={async () => {
									const changes = apiRef.current?.geometryChanges();
									if (!changes) return;
									const result = await saveGeometry({variables: {mapId: map.id, labels: changes.labels, paths: changes.paths}});
									if (result.data) setEditing(false);
								}}
							>
								Сохранить{editState.dirty ? ` (${editState.dirty})` : ''}
							</Button>
							<Button
								onClick={() => {
									apiRef.current?.revertGeometry();
									setEditing(false);
								}}
							>
								Отмена
							</Button>
							<Button
								appearance={placing ? 'primary' : 'subtle'}
								loading={creating}
								disabled={!!editState.dirty}
								title={editState.dirty ? 'Сначала сохраните правки точек' : undefined}
								aria-pressed={placing}
								onClick={() => setPlacing((on) => !on)}
							>
								Добавить метку
							</Button>
						</div>
					</div>
				)}
				{view?.pixelsPerMile && <MapScale pixelsPerMile={view.pixelsPerMile} />}
			</div>

			{selected && (
				<MapLabelCard
					label={selected}
					startEditing={editCardId === selected.id}
					onClose={() => {
						setSelectedId(null);
						setEditCardId(null);
					}}
					onDeleted={() => {
						setSelectedId(null);
						setEditCardId(null);
					}}
				/>
			)}
		</div>
	);
};

const MILES_PER_DAY = 24;

const MeasureText: React.FC<{miles: number}> = ({miles}) => {
	const days = miles / MILES_PER_DAY;
	return (
		<>
			<strong>{milesText(miles)}</strong> · {Math.round(miles * 1.6)} км · пешком {days < 1 ? 'меньше дня' : `≈ ${days.toFixed(1)} дн.`}
		</>
	);
};
